import { Box, Divider, Grid, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { SelectChangeEvent } from '@mui/material/Select/SelectInput';
import { useTranslation } from 'react-i18next';
import CustomDropdown from '../../CustomDropdown/CustomDropdown';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { updateCoachEventDetailsRequest } from '../../../redux/coach/coachSlice';
import {
  createOptions,
  formatBufferTimeValue,
  formatDayLimitValue,
  formatMinimumNoticeTimeValue,
} from '../../../utils/eventDetailsEnums/formatEnumValue';
import StyledButton from '../../Common/StyledButton';
import createPermissionCheckers from '../../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../../types/commonTypes';

// dropdown options
const bufferTimeSelectOptions = [0, 15, 30, 60];
const minimumNoticeTimeSelectOptions = [15, 30, 60, 120, 240, 480];
const dayLimitSelectOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 0];

export default function EventLimits() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const coachId = useAppSelector((state) => state.coach.coach?.id);
  const coachEventDetails = useAppSelector((state) => state.coach.coachEventDetails);
  const { coachEventDetailsLoading, updateCoachEventLoading } = useAppSelector(
    (state) => state.coach
  );

  const [bufferTime, setBufferTime] = useState<number>(0);
  const [minimumNoticeTime, setMinimumNoticeTime] = useState<number>(240);
  const [dayLimit, setDayLimit] = useState<number>(0);
  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;

  useEffect(() => {
    if (coachEventDetails) {
      setBufferTime(coachEventDetails.bufferTime);
      setMinimumNoticeTime(coachEventDetails.minimumNoticeTime);
      setDayLimit(coachEventDetails.dayLimit);
    }
  }, [coachEventDetails]);

  const handleBufferTimeChange = (event: SelectChangeEvent<unknown>) => {
    setBufferTime(event.target.value as number);
  };

  const handleMinimumNoticeTimeChange = (event: SelectChangeEvent<unknown>) => {
    setMinimumNoticeTime(event.target.value as number);
  };

  const handleDayLimitChange = (event: SelectChangeEvent<unknown>) => {
    setDayLimit(event.target.value as number);
  };

  const handleSave = () => {
    const updatedSettings = {
      coachId,
      bufferTime: parseInt(bufferTime.toString(), 10),
      minimumNoticeTime: parseInt(minimumNoticeTime.toString(), 10),
      dayLimit: parseInt(dayLimit.toString(), 10),
    };
    dispatch(updateCoachEventDetailsRequest(updatedSettings));
  };

  const bufferTimeOptions = createOptions(bufferTimeSelectOptions, formatBufferTimeValue);
  const minimumNoticeTimeOptions = createOptions(
    minimumNoticeTimeSelectOptions,
    formatMinimumNoticeTimeValue
  );
  const dayLimitOptions = createOptions(dayLimitSelectOptions, formatDayLimitValue);

  let loadingText: string;

  if (coachEventDetailsLoading) {
    loadingText = t('bookings.eventLimits.loading');
  } else if (updateCoachEventLoading) {
    loadingText = t('bookings.eventLimits.saving');
  } else {
    loadingText = t('bookings.eventLimits.save');
  }

  return (
    <Grid
      sx={{
        width: '651px',
        height: '513px',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '10px',
        gap: '24p',
        padding: '32px',
        boxShadow: '0px 4px 40px 0px #FFDDC859',
        backgroundColor: '#FFFFFF',
        marginTop: '24px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}
        >
          <Typography variant="meta" color="#2A3C67">
            {t(`bookings.eventLimits.bufferTime`)}
          </Typography>
          <CustomDropdown
            name="bufferTime"
            options={bufferTimeOptions}
            label=""
            value={bufferTime.toString()}
            onChange={handleBufferTimeChange}
            disabled={
              !checkPermissions(UserPermissionType.BOOKINGS_MODULE_MANAGE_COACH_CONFIGURATIONS)
            }
          />
          <Typography variant="meta" fontSize={11} color="#2A3C67">
            {t(`bookings.eventLimits.bufferTimeDesc`)}
          </Typography>
        </Box>
        <Divider sx={{ borderColor: '#FFDDC8' }} />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}
        >
          <Typography variant="meta" color="#2A3C67">
            {t(`bookings.eventLimits.minimumNotice`)}
          </Typography>
          <CustomDropdown
            name="minimumNotice"
            options={minimumNoticeTimeOptions}
            label=""
            value={minimumNoticeTime.toString()}
            onChange={handleMinimumNoticeTimeChange}
            disabled={
              !checkPermissions(UserPermissionType.BOOKINGS_MODULE_MANAGE_COACH_CONFIGURATIONS)
            }
          />
          <Typography variant="meta" fontSize={11} color="#2A3C67">
            {t(`bookings.eventLimits.minimumNoticeDesc`)}
          </Typography>
        </Box>
        <Divider sx={{ borderColor: '#FFDDC8' }} />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}
        >
          <Typography variant="meta" color="#2A3C67">
            {t(`bookings.eventLimits.dayLimit`)}
          </Typography>
          <CustomDropdown
            name="dayLimit"
            options={dayLimitOptions}
            label=""
            value={dayLimit.toString()}
            onChange={handleDayLimitChange}
            disabled={
              !checkPermissions(UserPermissionType.BOOKINGS_MODULE_MANAGE_COACH_CONFIGURATIONS)
            }
          />
          <Typography variant="meta" fontSize={11} color="#2A3C67">
            {t(`bookings.eventLimits.dayLimitDesc`)}
          </Typography>
        </Box>
        <Divider sx={{ borderColor: '#EEEEEE' }} />
        {checkPermissions(UserPermissionType.BOOKINGS_MODULE_MANAGE_COACH_CONFIGURATIONS) && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <StyledButton
              onClick={handleSave}
              paddingX={60}
              paddingY={14}
              loading={coachEventDetailsLoading || updateCoachEventLoading}
              loadingText={loadingText}
            >
              {t(`bookings.eventLimits.save`)}
            </StyledButton>
          </Box>
        )}
      </Box>
    </Grid>
  );
}
